import { changeUser } from '@braze/web-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFeatureFlagValue from '@/components/analytics/hooks/useFeatureFlagValue';
import { useAuthState } from '@/components/context/AuthContext';
import {
  initializeBraze,
  isBrazeInitialized,
  disableBraze,
  cleanupBrazeCompletely,
  hasBrazePersistence,
  isBrazeAllowed,
  isPushNotificationGranted,
} from '@/lib/analytics/braze';
import { useConsent } from '@/lib/consent';
import { NEXT_PUBLIC_BRAZE_ENABLED, NEXT_PUBLIC_BRAZE_TRACK_ANONYMOUS } from '@/lib/env';

/**
 * BrazeInitializer — gates browser Braze on comms consent, push permission, or storage consent.
 */
const BrazeInitializer = () => {
  const { user } = useAuthState();
  const { allowed: consentAllowed, hydrated: consentHydrated } = useConsent();
  const brazeEnabledRaw = useFeatureFlagValue('079-braze_sdk-enabled-web', null);
  const brazeLoggedInUsersOnly = useFeatureFlagValue('081-braze_sdk_users_only-enabled-web', false);
  const [pushPermissionGranted, setPushPermissionGranted] = useState(() =>
    isPushNotificationGranted()
  );
  const cleanupInProgressRef = useRef(false);
  const pendingCleanupRef = useRef(false);
  const brazeEnabledStateRef = useRef<boolean | null>(null);
  const brazeLoggedInUsersOnlyRef = useRef(false);
  const brazeAllowedRef = useRef(false);
  const abortCleanupRef = useRef(false);
  const hasBeenLoggedInRef = useRef(false);
  const lastIdentifiedUserIdRef = useRef<string | null>(null);
  const latestUserIdRef = useRef<string | null>(null);

  const brazeEnabled = useMemo(() => {
    if (brazeEnabledRaw === null || brazeEnabledRaw === undefined) {
      return null;
    }
    if (typeof brazeEnabledRaw === 'boolean') {
      return brazeEnabledRaw;
    }
    if (typeof brazeEnabledRaw === 'string') {
      const lower = brazeEnabledRaw.toLowerCase();
      if (lower === 'on' || lower === 'true') return true;
      if (lower === 'off' || lower === 'false') return false;
    }
    return false;
  }, [brazeEnabledRaw]);

  const identifyBrazeUser = useCallback(() => {
    if (!latestUserIdRef.current) {
      lastIdentifiedUserIdRef.current = null;
      return;
    }

    const userId = latestUserIdRef.current;
    if (lastIdentifiedUserIdRef.current === userId) {
      return;
    }

    changeUser(userId);
    lastIdentifiedUserIdRef.current = userId;
  }, []);

  const isAnonymousBlocked = useCallback(
    (userId: string | null | undefined) =>
      !userId &&
      !hasBeenLoggedInRef.current &&
      (brazeLoggedInUsersOnlyRef.current || !NEXT_PUBLIC_BRAZE_TRACK_ANONYMOUS),
    []
  );

  const canInitBraze = useCallback(
    (userId: string | null | undefined = latestUserIdRef.current) =>
      NEXT_PUBLIC_BRAZE_ENABLED &&
      brazeAllowedRef.current &&
      brazeEnabledStateRef.current === true &&
      !isAnonymousBlocked(userId),
    [isAnonymousBlocked]
  );

  const initBrazeIfAllowed = useCallback(() => {
    if (!isBrazeInitialized() && !cleanupInProgressRef.current) {
      const brazeInitialized = initializeBraze();

      if (brazeInitialized) {
        identifyBrazeUser();
      }
    } else if (isBrazeInitialized()) {
      identifyBrazeUser();
    }
  }, [identifyBrazeUser]);

  const teardownBraze = useCallback(() => {
    lastIdentifiedUserIdRef.current = null;

    const wasInitialized = isBrazeInitialized();
    const hasPersistence = hasBrazePersistence();
    const shouldCleanup = wasInitialized || hasPersistence;

    if (!shouldCleanup) {
      return;
    }

    if (wasInitialized) {
      disableBraze();
    }

    const runCleanup = () => {
      if (cleanupInProgressRef.current) {
        pendingCleanupRef.current = true;
        return;
      }

      cleanupInProgressRef.current = true;
      abortCleanupRef.current = false;
      pendingCleanupRef.current = false;

      cleanupBrazeCompletely().finally(() => {
        cleanupInProgressRef.current = false;

        const pending = pendingCleanupRef.current;
        const abort = abortCleanupRef.current;
        pendingCleanupRef.current = false;
        abortCleanupRef.current = false;

        if (canInitBraze() && (abort || !pending)) {
          initBrazeIfAllowed();
        } else if (pending) {
          runCleanup();
        }
      });
    };

    runCleanup();
  }, [canInitBraze, initBrazeIfAllowed]);

  const tryInitBraze = useCallback(() => {
    if (!NEXT_PUBLIC_BRAZE_ENABLED) {
      return;
    }

    if (brazeEnabled === false || !brazeAllowedRef.current) {
      teardownBraze();
      return;
    }

    if (brazeEnabled === null) {
      return;
    }

    const userId = user?.id ? user.id.toString() : null;

    if (!canInitBraze(userId)) {
      return;
    }

    if (cleanupInProgressRef.current) {
      abortCleanupRef.current = true;
      return;
    }

    initBrazeIfAllowed();
  }, [user?.id, brazeEnabled, canInitBraze, initBrazeIfAllowed, teardownBraze]);

  useEffect(() => {
    const updatePushPermission = () => {
      setPushPermissionGranted(isPushNotificationGranted());
    };

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener('focus', updatePushPermission);

    let permissionStatus: PermissionStatus | null = null;
    let isMounted = true;
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          if (!isMounted) {
            return;
          }
          permissionStatus = status;
          permissionStatus.onchange = updatePushPermission;
          updatePushPermission();
        })
        .catch(() => {
          // Some browsers do not expose notification permission through the Permissions API.
        });
    }

    return () => {
      isMounted = false;
      window.removeEventListener('focus', updatePushPermission);
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  useEffect(() => {
    if (user?.id) {
      hasBeenLoggedInRef.current = true;
    }

    brazeEnabledStateRef.current = brazeEnabled;
    brazeLoggedInUsersOnlyRef.current = brazeLoggedInUsersOnly;
    latestUserIdRef.current = user?.id ? user.id.toString() : null;
    brazeAllowedRef.current = isBrazeAllowed({
      consentAllowed,
      contactPermission: user?.contactPermission,
      pushPermissionGranted,
    });

    if (!consentHydrated) {
      return;
    }

    tryInitBraze();
  }, [
    user?.id,
    user?.contactPermission,
    consentAllowed,
    consentHydrated,
    pushPermissionGranted,
    brazeEnabled,
    brazeLoggedInUsersOnly,
    tryInitBraze,
  ]);

  return null;
};

export default BrazeInitializer;
